import React, { useEffect, useMemo, useCallback } from 'react';
import {
	projectTypeToCanonicalId,
	toProjectType,
} from '@atlassian/jira-common-constants/src/project-types.tsx';
import { getEndOfTrialMessageExperiment } from '@atlassian/jira-end-of-trial-message-shared-global-component/src/utils.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import {
	useIsScreenLocked,
	useLockScreenController,
} from '@atlassian/jira-lock-screen-experiment-common/src/controllers/index.tsx';
import {
	type SupportedProjectType,
	isSupportedProjectType,
	isTrialEndLockScreenEnabledForAnyProjectType,
	isTrialEndLockScreenEnabledForProject,
	getStatSigCohortAndExposureFunction,
} from '@atlassian/jira-lock-screen-experiment-common/src/utils.tsx';
import { AsyncLazyLockScreenExperiment } from '@atlassian/jira-lock-screen-experiment/src/async.tsx';
import { useProject } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/project/index.tsx';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import {
	STANDARD_EDITION,
	getEditionForProject,
	type ApplicationEdition,
} from '@atlassian/jira-shared-types/src/edition.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { useProductEntitlementDetails } from '@atlassian/jira-tenant-context-controller/src/components/product-entitlement-details/index.tsx';
import { PACKAGE_NAME, TEAM_NAME } from './constants.tsx';
import { useLockScreenService } from './services/lock-screen/index.tsx';
import { getLocalEligibility, transformError, useFeatureExposedEvent } from './utils.tsx';

const LockScreenExperimentSharedComponent = () => {
	const isScreenLocked = useIsScreenLocked();
	const [, lockScreenActions] = useLockScreenController();
	const fireFeatureExposed = useFeatureExposedEvent();
	const appEditions = useAppEditions();
	const productEntitlementDetails = useProductEntitlementDetails();
	const { data: project } = useProject();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const projectType = toProjectType(project?.type);
	const edition = projectType && getEditionForProject(projectType, appEditions);
	const productKey = projectType && projectTypeToCanonicalId(projectType);
	const entitlementDetails = productKey && productEntitlementDetails?.[productKey];
	const billingSourceSystem = entitlementDetails?.billingSourceSystem || 'HAMS';
	const entitlementId = entitlementDetails?.entitlementId;
	const { eligible, ineligibilityReason } = getLocalEligibility({
		projectType,
		edition,
		entitlementDetails,
		billingSourceSystem,
		entitlementId,
	});

	const prefetchAnalyticsAttributes = useMemo(
		() => ({
			edition,
			productKey,
			productEntitlementDetails,
		}),
		[edition, productKey, productEntitlementDetails],
	);

	const handleLockScreenServiceError = useCallback(
		(err: Error) => {
			// this will never be true as we only fetch supported projectTypes
			if (!isSupportedProjectType(projectType)) {
				return;
			}

			const error = transformError(err);

			fireFeatureExposed(
				'fetchEligibilityFailed',
				projectType,
				{
					...prefetchAnalyticsAttributes,
					error,
				},
				edition,
			);

			lockScreenActions.unlockScreen();
		},
		[projectType, fireFeatureExposed, prefetchAnalyticsAttributes, edition, lockScreenActions],
	);

	const {
		loading: loadingLockScreenDetails,
		data: lockScreenDetails,
		error: lockScreenDetailsError,
		fetch: fetchLockScreenDetails,
	} = useLockScreenService({
		entitlementId,
		onError: handleLockScreenServiceError,
	});

	const updateLockScreen = useCallback(
		async (
			supportedProjectType: SupportedProjectType,
			projectEdition: ApplicationEdition | undefined,
		) => {
			// unlock screen until we handle loading state or store data
			lockScreenActions.unlockScreen();

			const data = await fetchLockScreenDetails();
			const isOnVariationForTrialEndLockScreen = () => {
				const { cohort } = getStatSigCohortAndExposureFunction({
					projectType: supportedProjectType,
					edition: projectEdition,
				});
				return cohort === 'variation';
			};

			// if there is no data then an error occurred and is handled in handleLockScreenServiceError
			if (data) {
				if (!data.predunning) {
					fireFeatureExposed(
						'notInPreDunning',
						supportedProjectType,
						{
							...prefetchAnalyticsAttributes,
							lockScreenDetails: data,
						},
						projectEdition,
					);

					lockScreenActions.unlockScreen();
				} else if (!data.usageOverLimit) {
					fireFeatureExposed(
						'underUserLimit',
						supportedProjectType,
						{
							...prefetchAnalyticsAttributes,
							lockScreenDetails: data,
						},
						projectEdition,
					);

					lockScreenActions.unlockScreen();
				} else {
					const {
						isEnrolled: isEndOfTrialMessageExperimentEnrolled,
						cohort: endOfTrialCohort,
						fireStatsigExperimentExposure: fireStatsigEotExperimentExposure,
					} = getEndOfTrialMessageExperiment(supportedProjectType);

					if (isEndOfTrialMessageExperimentEnrolled && projectEdition === STANDARD_EDITION) {
						fireStatsigEotExperimentExposure();
						fireTrackAnalytics(createAnalyticsEvent({}), 'feature exposed', 'endOfTrialMessaging', {
							cohort: endOfTrialCohort,
							growthInitiative: 'transmuters',
							projectType: supportedProjectType,
							edition: projectEdition,
						});

						if (endOfTrialCohort === 'variation') {
							fireFeatureExposed(
								'isVariationForEOTModal',
								supportedProjectType,
								{
									...prefetchAnalyticsAttributes,
									lockScreenDetails: data,
								},
								projectEdition,
							);

							lockScreenActions.unlockScreen();

							return;
						}
					}

					fireFeatureExposed(
						null,
						supportedProjectType,
						{
							...prefetchAnalyticsAttributes,
							lockScreenDetails: data,
						},
						projectEdition,
					);

					if (isOnVariationForTrialEndLockScreen()) {
						lockScreenActions.lockScreen();
					} else {
						lockScreenActions.unlockScreen();
					}
				}
			}
		},
		[
			lockScreenActions,
			fetchLockScreenDetails,
			fireFeatureExposed,
			prefetchAnalyticsAttributes,
			createAnalyticsEvent,
		],
	);

	useEffect(() => {
		if (!isSupportedProjectType(projectType)) {
			lockScreenActions.unlockScreen();
		} else if (!isTrialEndLockScreenEnabledForProject({ projectType, edition })) {
			// this supports rollout % of different project types
			lockScreenActions.unlockScreen();
		} else if (!eligible) {
			fireFeatureExposed(ineligibilityReason, projectType, prefetchAnalyticsAttributes, edition);
			lockScreenActions.unlockScreen();
		} else {
			updateLockScreen(projectType, edition);
		}
	}, [
		projectType,
		eligible,
		ineligibilityReason,
		prefetchAnalyticsAttributes,
		fireFeatureExposed,
		lockScreenActions,
		updateLockScreen,
		edition,
	]);

	const analyticsAttributes = useMemo(
		() => ({
			...prefetchAnalyticsAttributes,
			lockScreenDetails,
		}),
		[prefetchAnalyticsAttributes, lockScreenDetails],
	);

	if (isScreenLocked) {
		return (
			<JSErrorBoundary
				id="lock-screen-experiment-async"
				fallback="unmount"
				onError={() => lockScreenActions.unlockScreen()}
				packageName={PACKAGE_NAME}
				teamName={TEAM_NAME}
			>
				<AsyncLazyLockScreenExperiment
					showSkeleton={loadingLockScreenDetails}
					showError={!!lockScreenDetailsError}
					predunningDaysLeft={lockScreenDetails?.predunningDaysLeft}
					isBillingAdmin={lockScreenDetails?.isBillingAdmin}
					isBillingUser={lockScreenDetails?.isBillingUser}
					transactionAccountId={lockScreenDetails?.transactionAccountId}
					invoiceGroupId={lockScreenDetails?.invoiceGroupId}
					usageOverLimit={lockScreenDetails?.usageOverLimit}
					entitlementId={entitlementId}
					projectType={projectType}
					analyticsAttributes={analyticsAttributes}
				/>
			</JSErrorBoundary>
		);
	}

	return null;
};

export const LockScreenExperimentShared = () => {
	const [, lockScreenActions] = useLockScreenController();
	const appEditions = useAppEditions();

	if (isTrialEndLockScreenEnabledForAnyProjectType(appEditions)) {
		return (
			<JSErrorBoundary
				id="lock-screen-experiment-shared"
				fallback="unmount"
				onError={() => lockScreenActions.unlockScreen()}
				packageName={PACKAGE_NAME}
				teamName={TEAM_NAME}
			>
				<LockScreenExperimentSharedComponent />
			</JSErrorBoundary>
		);
	}

	return null;
};

LockScreenExperimentShared.displayName = 'LockScreenExperimentShared';
